import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate
} from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import "./assets/css/reset.css";
import "./assets/css/style.css";

import Home from "./pages/Home";
import Mypage from "./pages/Mypage";
import Regular from "./pages/Regular";
import Payment from "./pages/Payment";
import Order from "./pages/Order";
import Point from "./pages/Point";
import UsePoint from "./pages/UsePoint";
import CardInfo from "./pages/CardInfo";
import UserInfo from "./pages/UserInfo";
import Account from "./pages/Account";
import EmailChange from "./pages/EmailChange";
import Contact from "./pages/Contact";
import ContactThanks from "./pages/ContactThanks";
import Products from "./pages/Products";
import Signup from "./pages/Signup";
import UserRegist from "./pages/UserRegist";
import NewRegist from "./pages/NewRegist";
import SignupThanks from "./pages/SignupThanks";
import Forget from "./pages/Forget";
import ResetPassword from "./pages/ResetPassword";
import ForgetThanks from "./pages/ForgetThanks";
import ChangeEmail from "./pages/ChangeEmail";
import ChangePassword from "./pages/ChangePassword";
import VerifyEmail from "./pages/VerifyEmail";

// ユーザー情報取得API
import { fetchGetUser } from "./api";

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // ログイン不要ページ一覧（副作用抑制・ログアウト対象）
  const publicPaths = useMemo(() => [
    "/", "/signup", "/signup-thanks",
    "/forget", "/forget-input", "/forget-thanks",
    "/change-email" // ← 追加
  ], []);

  // ログイン状態更新
  const updateLoginState = useCallback(() => {
    const token = localStorage.getItem("access_token");
    let customerType = localStorage.getItem("customer_type");
    const loginTriggered = localStorage.getItem("login_triggered") === "true";

    setIsLoggedIn(!!token);

    if (token) {
      fetchGetUser(token)
        .then((data) => {
          // 401: トークン無効 → ログアウト
          if (data?._unauthorized) {
            handleLogout();
            return;
          }
          if (data && data.customer_type) {
            customerType = data.customer_type;
          }
          if (data && data.customer_number) {
            setIsLoggedIn(true);
            if (data.customer_name) {
              setUserName(data.customer_name);
            }
          } else if (customerType === "new" || customerType === "existing") {
            // 未登録ユーザー → 登録画面へ誘導（ログイン状態は維持）
            // ログイン不要ページではリダイレクト抑制
            if (loginTriggered) {
              const target = customerType === "existing" ? "/userregist" : "/newregist";
              navigate(`${target}?token=${token}`);
              localStorage.removeItem("login_triggered");
            }
          } else {
            // customer_numberがlocalStorageにあれば通信の一時的な問題として維持
            const storedCustomerNumber = localStorage.getItem("customer_number");
            if (!storedCustomerNumber) {
              handleLogout();
            }
          }
        })
        .catch((error) => {
          // 通信エラー・サーバーエラー時はログイン状態を維持
          console.error("ユーザー情報取得に失敗:", error);
        });
    } else {
      handleLogout();
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("email");
    localStorage.removeItem("customer_number");
    localStorage.removeItem("login_triggered");
    setIsLoggedIn(false);
    setUserName("");
  };

  // 初回マウント時
  useEffect(() => {
    updateLoginState();
  }, [updateLoginState]);

  // 追従スクロールがある際のフッターの高さ調整
  useEffect(() => {
    const fixedEl = document.querySelector(".fixed");
    const footerEl = document.querySelector(".contact-section");

    if (fixedEl && footerEl) {
      const fixedHeight = fixedEl.offsetHeight;
      footerEl.style.marginBottom = `${fixedHeight}px`;
    }
  }, []);

  // localStorage 変更時
  useEffect(() => {
    const handleStorageChange = () => {
      updateLoginState();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [updateLoginState]);

  // ログイン不要ページにアクセスしたら強制ログアウト
  useEffect(() => {
    if (publicPaths.includes(location.pathname)) {
      handleLogout();
    }
  }, [location.pathname, publicPaths]);

  let screenClass = isLoggedIn ? "mypage" : "logout";
  // newregist / userregist / signup-thanks は強制的に logout 表示
  if (["/newregist", "/userregist", "/signup-thanks", "/change-email"].includes(location.pathname)) {
    screenClass = "logout";
  }
  // マイページだけ追加で top クラス
  if (location.pathname === "/mypage") {
    screenClass += " top";
  }

  // 認証必須ページ用ラッパー
  const RequireAuth = ({ children }) => {
    const token = localStorage.getItem("access_token");
    const customerNumber = localStorage.getItem("customer_number");
    const customerType = localStorage.getItem("customer_type");

    if (!token) return <Navigate to="/" replace />;

    if (!customerNumber) {
      const target = customerType === "existing" ? "/userregist" : "/newregist";
      return <Navigate to={`${target}?token=${token}`} replace />;
    }

    return children;
  };

  return (
    <div className={`screen ${screenClass}`}>
      <main className="main">
        <Header isLoggedIn={isLoggedIn} userName={userName} onLogout={handleLogout} />
        <div className="wrapper">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/mypage" element={<RequireAuth><Mypage /></RequireAuth>} />
            <Route path="/regular" element={<RequireAuth><Regular /></RequireAuth>} />
            <Route path="/payment/:subscription_number" element={<RequireAuth><Payment /></RequireAuth>} />
            <Route path="/order" element={<RequireAuth><Order /></RequireAuth>} />
            <Route path="/point" element={<RequireAuth><Point /></RequireAuth>} />
            <Route path="/use/:subscription_number" element={<RequireAuth><UsePoint /></RequireAuth>} />
            <Route path="/cardinfo" element={<RequireAuth><CardInfo /></RequireAuth>} />
            <Route path="/userinfo" element={<RequireAuth><UserInfo /></RequireAuth>} />
            <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
            <Route path="/email" element={<RequireAuth><EmailChange /></RequireAuth>} />
            <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
            <Route path="/contact" element={<RequireAuth><Contact /></RequireAuth>} />
            <Route path="/contact-thanks" element={<RequireAuth><ContactThanks /></RequireAuth>} />
            <Route path="/products" element={<RequireAuth><Products /></RequireAuth>} />
            <Route path="/change-email" element={<ChangeEmail />} />
            {/* 非ログインでもアクセス可能なページ */}
            <Route path="/signup" element={<Signup />} />
            <Route path="/userregist" element={<UserRegist />} />
            <Route path="/newregist" element={<NewRegist />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/signup-thanks" element={<SignupThanks />} />
            <Route path="/forget" element={<Forget />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forget-thanks" element={<ForgetThanks />} />
          </Routes>
        </div>
        <Footer />
      </main>
    </div>
  );
};

const App = () => (
  <Router>
    <AppContent />
  </Router>
);

export default App;
